import React, { useState } from 'react';
import { getDispatchItemProductName } from '../utils/dispatchDisplay';

export default function DraftDispatchEditor({ order, inventoryProducts, onSave, onClose }) {
  const [items, setItems] = useState(() => (order.items || []).map(item => ({ ...item,
    no_of_units: item.no_of_units ?? item.qty,
    exp_date: String(item.exp_date_text || item.exp_date || '').slice(0, 10),
  })));
  const [search, setSearch] = useState('');
  const [selectedStock, setSelectedStock] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const warehouse = String(order.source || '').split(':')[1];
  const stock = inventoryProducts.filter(row => String(row.warehouse_id) === warehouse && row.is_active !== false);
  const available = item => {
    const row = stock.find(row => String(row.id) === String(item.inventory_product_id));
    return Number(row?.count_in_stock ?? row?.no_of_units ?? 0);
  };
  // Every typed word must appear somewhere in the product, brand, pack or barcodes ("toor 1 kg" works).
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const searchText = row => [row.product_name, row.brand_name, row.brand_name_english, row.brand,
    `${row.quantity || row.barcode_quantity || ''} ${row.unit_short_code || ''}`, row.mk_barcode, row.bar_code, row.vendor_barcode, row.sku_id]
    .map(value => String(value || '').toLowerCase()).join(' ');
  const matches = stock.filter(row => Number(row.count_in_stock ?? row.no_of_units ?? 0) > 0
    && !items.some(item => String(item.inventory_product_id) === String(row.id))
    && words.every(word => searchText(row).includes(word))).slice(0, 30);
  const suggestions = words.length ? matches.slice(0, 10) : [];
  const describe = row => `${row.quantity || row.barcode_quantity || ''} ${row.unit_short_code || ''} · ${row.mk_barcode || row.bar_code || ''} · Available ${row.count_in_stock ?? row.no_of_units}${row.exp_date ? ` · Exp ${String(row.exp_date).slice(0, 10)}` : ''}`;
  const addStock = row => {
    if (row) setItems(current => [...current, { ...row, id: undefined, inventory_product_id: row.id,
      no_of_units: 1, qty: 1, exp_date: String(row.exp_date || '').slice(0, 10), notes: '' }]);
    setSearch(''); setSelectedStock(''); setHighlight(0);
  };
  const change = (index, field, value) => setItems(rows => rows.map((row, i) => i === index ? { ...row, [field]: value } : row));
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    if (!items.length) { setError('Add at least one item, or close this editor and delete the draft.'); return; }
    const invalid = items.find(item => !item.inventory_product_id || !item.exp_date
      || !Number.isFinite(Number(item.no_of_units)) || Number(item.no_of_units) <= 0 || Number(item.no_of_units) > available(item));
    if (invalid) { setError(`Check expiry and quantity for ${getDispatchItemProductName(invalid)}. Available: ${available(invalid)}.`); return; }
    setBusy(true); setError('');
    try {
      await onSave(items.map(item => ({ ...item, qty: Number(item.no_of_units), no_of_units: Number(item.no_of_units) })));
      onClose();
    } catch (err) { setError(err?.message || String(err)); }
    finally { setBusy(false); }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Edit draft dispatch">
    <form onSubmit={save} className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-xl bg-white p-5">
      <h2 className="text-xl font-bold">Edit draft {order.dispatch_no}</h2>
      <p className="my-2 text-sm text-gray-600">Change quantities, expiry or notes, remove items, or add stock from this warehouse. Prices are recalculated when you update the draft.</p>
      {error && <p role="alert" className="my-2 text-red-700">{error}</p>}
      <fieldset disabled={busy}>
        <label className="block">Add item: search product or barcode
          <input value={search} placeholder="Type product name, pack or barcode" autoComplete="off"
            role="combobox" aria-expanded={suggestions.length > 0} aria-controls="draft-stock-suggestions"
            onChange={e => { setSearch(e.target.value); setHighlight(0); }}
            onKeyDown={e => {
              if (!suggestions.length) return;
              if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(i => Math.min(i + 1, suggestions.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(i => Math.max(i - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); addStock(suggestions[highlight]); }
              else if (e.key === 'Escape') setSearch('');
            }}
            className="ml-2 w-96 max-w-full rounded border p-2" />
        </label>
        {words.length > 0 && <ul id="draft-stock-suggestions" role="listbox" aria-label="Stock suggestions"
          className="mt-1 max-h-72 overflow-auto rounded border bg-white shadow">
          {suggestions.map((row, i) => <li key={row.id} role="option" aria-selected={i === highlight}
            onMouseEnter={() => setHighlight(i)} onMouseDown={e => { e.preventDefault(); addStock(row); }}
            className={`cursor-pointer px-3 py-2 ${i === highlight ? 'bg-blue-50' : ''}`}>
            <div className="font-medium">{row.product_name}</div>
            <div className="text-xs text-gray-600">{describe(row)} · Stock #{row.id}</div>
          </li>)}
          {!suggestions.length && <li className="px-3 py-2 text-sm text-gray-500">No stock in this warehouse matches "{search}".</li>}
        </ul>}
        <select aria-label="Stock item to add" value={selectedStock} className="my-2 w-full rounded border p-2" onChange={e => setSelectedStock(e.target.value)}>
          <option value="">Select stock to add</option>
          {matches.map(row => <option key={row.id} value={row.id}>{row.product_name} · {row.quantity || row.barcode_quantity} {row.unit_short_code} · {row.mk_barcode || row.bar_code} · Stock #{row.id} · Available {row.count_in_stock ?? row.no_of_units}</option>)}
        </select>
        <button type="button" disabled={!selectedStock} className="mb-3 rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
          onClick={() => addStock(stock.find(row => String(row.id) === selectedStock))}>Add product</button>
        <div className="overflow-x-auto"><table className="w-full text-left"><thead><tr>
          {['Product', 'Available', 'Quantity', 'Expiry', 'Notes', ''].map((title, i) => <th className="p-2" key={i}>{title}</th>)}
        </tr></thead><tbody>{items.map((item, index) => <tr key={item.inventory_product_id || item.id || index}>
          <td className="p-2">{getDispatchItemProductName(item)}<div className="text-xs">{item.mk_barcode || item.product_barcode_id}</div>
            <select aria-label={`Replace product item ${index + 1}`} value="" className="mt-1 max-w-xs rounded border p-1" onChange={e => {
              const replacement = stock.find(row => String(row.id) === e.target.value);
              if (!replacement) return;
              setItems(current => current.map((row, i) => i !== index ? row : { ...replacement, id: undefined,
                inventory_product_id: replacement.id, no_of_units: row.no_of_units, qty: row.no_of_units,
                exp_date: String(replacement.exp_date || '').slice(0, 10), notes: '' }));
            }}><option value="">Change product / pack</option>
              {stock.filter(row => Number(row.count_in_stock ?? row.no_of_units ?? 0) > 0
                && !items.some(current => String(current.inventory_product_id) === String(row.id)))
                .map(row => <option key={row.id} value={row.id}>{row.product_name} · {row.quantity || row.barcode_quantity} {row.unit_short_code} · #{row.id}</option>)}
            </select>
          </td>
          <td className="p-2">{available(item)}</td>
          <td><input aria-label={`Quantity item ${index + 1}`} type="number" min="0.001" step="any" required value={item.no_of_units}
            onChange={e => change(index, 'no_of_units', e.target.value)} className="w-24 rounded border p-2" /></td>
          <td><input aria-label={`Expiry item ${index + 1}`} type="date" required value={item.exp_date} onChange={e => change(index, 'exp_date', e.target.value)} className="rounded border p-2" /></td>
          <td><input aria-label={`Notes item ${index + 1}`} value={item.notes || ''} onChange={e => change(index, 'notes', e.target.value)} className="w-36 rounded border p-2" /></td>
          <td><button type="button" onClick={() => setItems(current => current.filter((_, i) => i !== index))} className="p-2 text-red-700">Remove item {index + 1}</button></td>
        </tr>)}</tbody></table></div>
      </fieldset>
      <div className="mt-4 flex justify-end gap-3">
        <button type="button" disabled={busy} onClick={onClose} className="rounded border px-4 py-2">Cancel editing</button>
        <button type="submit" disabled={busy || !items.length} className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{busy ? 'Updating…' : 'Update products'}</button>
      </div>
    </form>
  </div>;
}
